import type { DataChoice, Localization } from '../types'

import React, { useMemo } from 'react'
import { uniq, uniqBy } from 'lodash/fp'

import {
	MultiSelect,
	MultiSelectCheckboxItem,
	MultiSelectCheckboxSelectAll,
	MultiSelectContent,
	MultiSelectEmpty,
	MultiSelectGroup,
	MultiSelectList,
	MultiSelectSearch,
	MultiSelectTrigger,
	MultiSelectValue,
	useMultiSelect,
} from '@/shared/components/ui/multi-select'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'

export default function CreatebleMultiSelectField({
	ref,
	disabled,
	onValueChange,
	defaultValue,
	value,
	options,
	className,
	placeholder,
	invalid,
	isLoading,
	locale,
}: {
	ref?: React.Ref<HTMLButtonElement>
	defaultValue?: string[]
	isLoading?: boolean
	disabled?: boolean
	onValueChange: (value: string[]) => void
	value: string[]
	options: DataChoice[]
	className?: string
	placeholder?: string
	invalid?: boolean
	locale: Localization
}) {
	return (
		<MultiSelect
			isLoading={isLoading}
			disabled={disabled}
			defaultValue={defaultValue}
			onSearch={() => {}}
			onValueChange={onValueChange}
			value={value}
			options={options}
		>
			<MultiSelectTrigger
				ref={ref}
				aria-invalid={invalid}
				className={className}
			>
				<MultiSelectValue maxDisplay={2} placeholder={placeholder} />
			</MultiSelectTrigger>
			<MultiSelectContent className='max-h-[var(--radix-popover-content-available-height)]'>
				<MultiSelectVirtualContent
					locale={locale}
					value={value}
					options={options}
				/>
			</MultiSelectContent>
		</MultiSelect>
	)
}

function MultiSelectVirtualContent<T extends DataChoice>({
	options,
	value,
	locale,
}: {
	options: T[]
	value: string[]
	locale: Localization
}) {
	const { onSelectAll } = useMultiSelect()
	const [searchText, setSearchText] = React.useState('')
	const { filteredOptions } = useCreatebleMultiSelectOptions({
		options,
		value,
		searchText,
	})

	return (
		<>
			<MultiSelectSearch
				onValueChange={setSearchText}
				value={searchText}
				placeholder={locale?.getSearchPlaceholder?.() || 'Input to search'}
				onPaste={(e) => {
					const text = e.clipboardData.getData('text/plain')
					const pastedValue = text
						.split('\n')
						.map((line) => line.trim())
						.filter((v) => v !== '')
					if (pastedValue?.length > 1) {
						e.preventDefault()
						onSelectAll(uniq([...(value || []), ...pastedValue]))
					}
				}}
			/>
			<Virtualized asChild>
				<MultiSelectList className='scroll-pt-12'>
					<MultiSelectEmpty />
					{filteredOptions.length > 1 && (
						<MultiSelectGroup className='sticky top-0 z-10 bg-popover'>
							<MultiSelectCheckboxSelectAll
								values={filteredOptions.map(({ value }) => value)}
							>
								{locale.getSelectAllMessage?.() || 'Select all'}
							</MultiSelectCheckboxSelectAll>
						</MultiSelectGroup>
					)}
					<MultiSelectGroup>
						<VirtualizedVirtualizer>
							{filteredOptions.map((option) => (
								<MultiSelectCheckboxItem
									key={option.value}
									keywords={[option.label, option.value]}
									value={option.value}
								>
									{option.label}
								</MultiSelectCheckboxItem>
							))}
						</VirtualizedVirtualizer>
					</MultiSelectGroup>
				</MultiSelectList>
			</Virtualized>
		</>
	)
}

export const useCreatebleMultiSelectOptions = ({
	options,
	value,
	searchText,
}: {
	options: DataChoice[]
	value: string[]
	searchText: string
}) => {
	const filteredOptions = useMemo(() => {
		const optionsValues = options.map((o) => o.value)
		const notExistedValues = value.filter(
			(v) => optionsValues.indexOf(v) === -1
		)
		const filledOptions = uniqBy('value', [
			...notExistedValues.map((v) => ({
				label: v,
				value: v,
			})),
			...options,
		])
		if (!searchText) return filledOptions
		const filtered = filledOptions.filter((option) =>
			option.label.toLowerCase().includes(searchText.toLowerCase() ?? [])
		)
		if (!filtered.length) {
			return [
				{
					label: searchText,
					value: searchText,
				},
			]
		}

		return uniqBy('value', filtered)
	}, [options, searchText, value])

	return {
		filteredOptions,
	}
}
